import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createShow, createScheduledShows, listShows, ApiError } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { ImageUpload } from './ImageUpload';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Calendar, Clock } from 'lucide-react';
import { colors } from '../constants/colors';

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
const DAY_VALUES = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;

function calculateShowCount(config: {
  firstShowAt: Date;
  repeatType: 'DAILY' | 'WEEKLY';
  intervalDays: number;
  weekdaysOnly: boolean;
  intervalWeeks: number;
  selectedDays: string[];
  stopType: 'COUNT' | 'DATE';
  stopAfterCount: number;
  stopAfterDate: Date;
}): number {
  const { firstShowAt, repeatType, intervalDays, weekdaysOnly, intervalWeeks, selectedDays, stopType, stopAfterCount, stopAfterDate } = config;
  const maxCount = stopType === 'COUNT' ? Math.min(stopAfterCount, 30) : 30;
  let count = 1; // first show

  const startDate = new Date(firstShowAt);
  startDate.setHours(0, 0, 0, 0);

  if (repeatType === 'DAILY') {
    if (selectedDays.length > 0) {
      const daySet = new Set(selectedDays);
      const current = new Date(startDate);
      current.setDate(current.getDate() + 1);
      while (count < maxCount) {
        if (stopType === 'DATE' && current > stopAfterDate) break;
        const dow = DAY_VALUES[((current.getDay() + 6) % 7)];
        if (daySet.has(dow)) count++;
        current.setDate(current.getDate() + 1);
      }
    } else if (weekdaysOnly) {
      const current = new Date(startDate);
      current.setDate(current.getDate() + 1);
      while (count < maxCount) {
        if (stopType === 'DATE' && current > stopAfterDate) break;
        const dow = current.getDay();
        if (dow >= 1 && dow <= 5) count++;
        current.setDate(current.getDate() + 1);
      }
    } else {
      const current = new Date(startDate);
      current.setDate(current.getDate() + intervalDays);
      while (count < maxCount) {
        if (stopType === 'DATE' && current > stopAfterDate) break;
        count++;
        current.setDate(current.getDate() + intervalDays);
      }
    }
  } else if (repeatType === 'WEEKLY') {
    const daySet = new Set(selectedDays.length > 0 ? selectedDays : [DAY_VALUES[((startDate.getDay() + 6) % 7)]]);
    // Remaining days in first week
    const current = new Date(startDate);
    current.setDate(current.getDate() + 1);
    const firstWeekEnd = new Date(startDate);
    firstWeekEnd.setDate(firstWeekEnd.getDate() + (7 - firstWeekEnd.getDay()) % 7);
    if (firstWeekEnd <= startDate) firstWeekEnd.setDate(firstWeekEnd.getDate() + 7);

    while (current <= firstWeekEnd && count < maxCount) {
      if (stopType === 'DATE' && current > stopAfterDate) return count;
      const dow = DAY_VALUES[((current.getDay() + 6) % 7)];
      if (daySet.has(dow)) count++;
      current.setDate(current.getDate() + 1);
    }

    // Subsequent weeks
    const weekStart = new Date(firstWeekEnd);
    weekStart.setDate(weekStart.getDate() + (intervalWeeks - 1) * 7);
    let safety = 0;
    while (count < maxCount && safety < 500) {
      safety++;
      for (let d = 0; d < 7 && count < maxCount; d++) {
        const candidate = new Date(weekStart);
        candidate.setDate(candidate.getDate() + d);
        if (stopType === 'DATE' && candidate > stopAfterDate) return count;
        const dow = DAY_VALUES[((candidate.getDay() + 6) % 7)];
        if (daySet.has(dow)) count++;
      }
      weekStart.setDate(weekStart.getDate() + intervalWeeks * 7);
    }
  }

  return Math.min(count, 30);
}

export function CreateShow() {
  useDocumentTitle('Go Live');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isScheduleMode = searchParams.get('mode') === 'schedule';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeShow, setActiveShow] = useState<{ id: string; name: string } | null>(null);

  // Form state
  const [showName, setShowName] = useState(`Live Sale ${new Date().toLocaleDateString()}`);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [useCustomItemNumber, setUseCustomItemNumber] = useState(false);
  const [startingItemNumber, setStartingItemNumber] = useState('1');

  // Schedule state - default to tomorrow at noon
  const getDefaultScheduleDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);
    return tomorrow;
  };

  const [scheduleDate, setScheduleDate] = useState<Date>(getDefaultScheduleDate());

  // Recurrence state
  const [repeatType, setRepeatType] = useState<'NONE' | 'DAILY' | 'WEEKLY'>('NONE');
  const [intervalDays, setIntervalDays] = useState(1);
  const [intervalWeeks, setIntervalWeeks] = useState(1);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [dailyMode, setDailyMode] = useState<'interval' | 'weekdays' | 'specific'>('interval');
  const [stopType, setStopType] = useState<'COUNT' | 'DATE'>('COUNT');
  const [stopAfterCount, setStopAfterCount] = useState(4);
  const [stopAfterDate, setStopAfterDate] = useState<Date>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d;
  });

  const previewCount = useMemo(() => {
    if (repeatType === 'NONE') return null;
    return calculateShowCount({
      firstShowAt: scheduleDate,
      repeatType,
      intervalDays,
      weekdaysOnly: dailyMode === 'weekdays',
      intervalWeeks,
      selectedDays: dailyMode === 'specific' ? selectedDays : (repeatType === 'WEEKLY' ? selectedDays : []),
      stopType,
      stopAfterCount,
      stopAfterDate,
    });
  }, [repeatType, scheduleDate, intervalDays, dailyMode, intervalWeeks, selectedDays, stopType, stopAfterCount, stopAfterDate]);

  // Check for existing live shows on mount (non-schedule mode only)
  useEffect(() => {
    if (isScheduleMode || !user?.id) return;
    listShows('LIVE', user.id)
      .then((shows) => {
        if (shows.length > 0) {
          setActiveShow({ id: shows[0].id, name: shows[0].name });
        }
      })
      .catch((e) => console.error('Failed to check live shows:', e));
  }, [isScheduleMode, user?.id]);

  const toggleDay = (day: string) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleCreateShow = async () => {
    if (!showName.trim()) {
      setError('Please enter a show name');
      return;
    }

    if (isScheduleMode && scheduleDate <= new Date()) {
      setError('Scheduled time must be in the future');
      return;
    }

    if (activeShow && !isScheduleMode) return;

    setLoading(true);
    setError(null);
    try {
      if (isScheduleMode && repeatType !== 'NONE') {
        // Batch create recurring shows
        await createScheduledShows(
          showName.trim(),
          scheduleDate,
          {
            repeatType,
            intervalDays: repeatType === 'DAILY' && dailyMode === 'interval' ? intervalDays : undefined,
            weekdaysOnly: repeatType === 'DAILY' && dailyMode === 'weekdays',
            intervalWeeks: repeatType === 'WEEKLY' ? intervalWeeks : undefined,
            selectedDays: (repeatType === 'DAILY' && dailyMode === 'specific') || repeatType === 'WEEKLY' ? selectedDays : undefined,
            stopType,
            stopAfterCount: stopType === 'COUNT' ? stopAfterCount : undefined,
            stopAfterDate: stopType === 'DATE' ? stopAfterDate.toISOString().split('T')[0] : undefined,
          },
          thumbnailUrl.trim() || undefined,
        );
        navigate('/sell');
      } else {
        const newShow = await createShow(
          showName.trim(),
          thumbnailUrl.trim() || undefined,
          isScheduleMode ? scheduleDate : undefined,
          useCustomItemNumber ? Math.max(1, Math.min(50000, parseInt(startingItemNumber) || 1)) : undefined
        );

        if (isScheduleMode) {
          navigate('/sell');
        } else {
          navigate(`/sell/live/${newShow.id}`);
        }
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && e.data.activeShowId) {
        setActiveShow({ id: e.data.activeShowId, name: 'your live show' });
        setLoading(false);
        return;
      }
      setError(e instanceof Error ? e.message : 'Failed to create show');
      console.error(e);
      setLoading(false);
    }
  };

  // Format date for datetime-local input
  const formatDateTimeLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeLocal = (value: string) => {
    return new Date(value);
  };

  const formatScheduleDisplay = (date: Date) => {
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatDateLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const buttonLabel = () => {
    if (loading) {
      return repeatType !== 'NONE' && isScheduleMode ? 'Scheduling Shows...' : isScheduleMode ? 'Scheduling...' : 'Creating...';
    }
    if (repeatType !== 'NONE' && isScheduleMode && previewCount) {
      return `Schedule ${previewCount} Shows`;
    }
    return isScheduleMode ? 'Schedule Show' : 'Go Live';
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerLeft}>
          <button onClick={() => navigate('/sell')} style={styles.backLink}>
            &larr; Back to Seller Hub
          </button>
        </div>
        <h1 style={styles.title}>
          {isScheduleMode ? 'Schedule a Show' : 'Go Live Now'}
        </h1>
        <div style={styles.headerRight} />
      </header>

      <main style={styles.main}>
        <div style={styles.formCard}>
          {/* Mode Toggle */}
          <div style={styles.modeToggle}>
            <button
              onClick={() => navigate('/sell/live')}
              style={{
                ...styles.modeButton,
                ...(!isScheduleMode ? styles.modeButtonActive : {}),
              }}
            >
              <Clock size={18} />
              Go Live Now
            </button>
            <button
              onClick={() => navigate('/sell/live?mode=schedule')}
              style={{
                ...styles.modeButton,
                ...(isScheduleMode ? styles.modeButtonActive : {}),
              }}
            >
              <Calendar size={18} />
              Schedule Show
            </button>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Show Name *</label>
            <input
              type="text"
              value={showName}
              onChange={(e) => setShowName(e.target.value)}
              placeholder="Enter show name"
              style={styles.input}
            />
          </div>

          {isScheduleMode && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label}>Schedule Date & Time *</label>
                <input
                  type="datetime-local"
                  value={formatDateTimeLocal(scheduleDate)}
                  onChange={(e) => setScheduleDate(parseDateTimeLocal(e.target.value))}
                  min={formatDateTimeLocal(new Date())}
                  style={styles.input}
                />
                <p style={styles.schedulePreview}>
                  {formatScheduleDisplay(scheduleDate)}
                </p>
              </div>

              {/* Repeats selector */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Repeats</label>
                <select
                  value={repeatType}
                  onChange={(e) => setRepeatType(e.target.value as 'NONE' | 'DAILY' | 'WEEKLY')}
                  style={styles.input}
                >
                  <option value="NONE">Does not repeat</option>
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                </select>
              </div>

              {/* Daily config */}
              {repeatType === 'DAILY' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Frequency</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input type="radio" name="dailyMode" checked={dailyMode === 'interval'} onChange={() => setDailyMode('interval')} />
                      <span style={{ marginLeft: 6 }}>Daily</span>
                    </label>
                    {dailyMode === 'interval' && (
                      <div style={{ marginLeft: 28, display: 'flex', alignItems: 'center' }}>
                        <span style={{ fontSize: '14px', color: '#333' }}>Every</span>
                        <input
                          type="number"
                          min={1}
                          max={30}
                          value={intervalDays}
                          onChange={(e) => setIntervalDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
                          style={{ ...styles.numberInput, marginLeft: 8, marginRight: 8 }}
                        />
                        <span style={{ fontSize: '14px', color: '#333' }}>{intervalDays === 1 ? 'day' : 'days'}</span>
                      </div>
                    )}
                    <label style={styles.radioLabel}>
                      <input type="radio" name="dailyMode" checked={dailyMode === 'specific'} onChange={() => setDailyMode('specific')} />
                      <span style={{ marginLeft: 6 }}>Specific days</span>
                    </label>
                  </div>
                  {dailyMode === 'specific' && (
                    <div style={styles.dayChips}>
                      {DAY_LABELS.map((label, i) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => toggleDay(DAY_VALUES[i])}
                          style={{
                            ...styles.dayChip,
                            ...(selectedDays.includes(DAY_VALUES[i]) ? styles.dayChipActive : {}),
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Weekly config */}
              {repeatType === 'WEEKLY' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>
                    Every
                    <input
                      type="number"
                      min={1}
                      max={12}
                      value={intervalWeeks}
                      onChange={(e) => setIntervalWeeks(Math.max(1, Math.min(12, parseInt(e.target.value) || 1)))}
                      style={{ ...styles.numberInput, marginLeft: 8, marginRight: 8 }}
                    />
                    {intervalWeeks === 1 ? 'week' : 'weeks'} on
                  </label>
                  <div style={styles.dayChips}>
                    {DAY_LABELS.map((label, i) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => toggleDay(DAY_VALUES[i])}
                        style={{
                          ...styles.dayChip,
                          ...(selectedDays.includes(DAY_VALUES[i]) ? styles.dayChipActive : {}),
                        }}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Stop condition */}
              {repeatType !== 'NONE' && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>Ends</label>
                  <div style={styles.radioGroup}>
                    <label style={styles.radioLabel}>
                      <input type="radio" name="stopType" checked={stopType === 'COUNT'} onChange={() => setStopType('COUNT')} />
                      <span style={{ marginLeft: 6 }}>After</span>
                      {stopType === 'COUNT' && (
                        <input
                          type="number"
                          min={2}
                          max={30}
                          value={stopAfterCount}
                          onChange={(e) => setStopAfterCount(Math.max(2, Math.min(30, parseInt(e.target.value) || 2)))}
                          style={{ ...styles.numberInput, marginLeft: 8, marginRight: 8 }}
                        />
                      )}
                      <span>shows</span>
                    </label>
                    <label style={styles.radioLabel}>
                      <input type="radio" name="stopType" checked={stopType === 'DATE'} onChange={() => setStopType('DATE')} />
                      <span style={{ marginLeft: 6 }}>On date</span>
                      {stopType === 'DATE' && (
                        <input
                          type="date"
                          value={formatDateLocal(stopAfterDate)}
                          onChange={(e) => setStopAfterDate(new Date(e.target.value))}
                          min={formatDateLocal(new Date())}
                          style={{ ...styles.input, marginLeft: 8, flex: 1 }}
                        />
                      )}
                    </label>
                  </div>
                </div>
              )}

            </>
          )}

          <div style={styles.formGroup}>
            <ImageUpload
              value={thumbnailUrl || null}
              onChange={(url) => setThumbnailUrl(url || '')}
              folder="shows"
              label="Thumbnail Image (optional)"
            />
          </div>

          {/* Custom Starting Item Number */}
          {!isScheduleMode && (
            <div style={{ marginTop: '20px' }}>
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                  Start with custom item number
                </span>
                <input
                  type="checkbox"
                  checked={useCustomItemNumber}
                  onChange={(e) => setUseCustomItemNumber(e.target.checked)}
                  style={{ width: '20px', height: '20px', accentColor: '#7C3AED' }}
                />
              </label>
              {useCustomItemNumber && (
                <div style={{ marginTop: '8px' }}>
                  <input
                    type="number"
                    value={startingItemNumber}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, '');
                      if (val === '' || (parseInt(val) >= 0 && parseInt(val) <= 50000)) {
                        setStartingItemNumber(val);
                      }
                    }}
                    min={1}
                    max={50000}
                    placeholder="1"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      fontSize: '16px',
                      borderRadius: '12px',
                      border: '1px solid #E5E7EB',
                      backgroundColor: '#F9FAFB',
                      outline: 'none',
                    }}
                  />
                  <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
                    Items will be numbered starting from this value (max 50,000)
                  </p>
                </div>
              )}
            </div>
          )}

          {activeShow && !isScheduleMode && (
            <div style={styles.activeShowBanner}>
              <p style={styles.activeShowText}>
                You already have a live show in progress.
              </p>
              <button
                onClick={() => navigate(`/sell/live/${activeShow.id}`)}
                style={styles.activeShowButton}
              >
                Join Current Stream
              </button>
            </div>
          )}

          {error && <p style={styles.error}>{error}</p>}

          <button
            onClick={handleCreateShow}
            disabled={loading}
            style={{
              ...styles.primaryButton,
              ...(isScheduleMode ? styles.scheduleButton : {}),
            }}
          >
            {buttonLabel()}
          </button>

          {isScheduleMode && repeatType === 'NONE' && (
            <p style={styles.helperText}>
              Your show will appear in the upcoming shows list. You can start it early from your Seller Hub.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.gray[100],
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: `1px solid ${colors.gray[300]}`,
    backgroundColor: colors.white,
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flex: 1,
  },
  backLink: {
    background: 'none',
    border: 'none',
    color: '#666',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '8px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    margin: 0,
    color: colors.ink.black,
  },
  main: {
    display: 'flex',
    justifyContent: 'center',
    padding: '48px 24px',
  },
  formCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    padding: '32px',
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    width: '100%',
    maxWidth: '480px',
  },
  modeToggle: {
    display: 'flex',
    gap: '8px',
    padding: '4px',
    backgroundColor: '#F3F4F6',
    borderRadius: '10px',
  },
  modeButton: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: '600',
    backgroundColor: 'transparent',
    color: colors.gray[600],
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  modeButtonActive: {
    backgroundColor: colors.white,
    color: colors.ink.black,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333',
    display: 'flex',
    alignItems: 'center',
  },
  input: {
    padding: '12px 16px',
    fontSize: '15px',
    border: '1px solid #d0d0d0',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  numberInput: {
    width: '60px',
    padding: '8px 12px',
    fontSize: '15px',
    border: '1px solid #d0d0d0',
    borderRadius: '8px',
    outline: 'none',
    textAlign: 'center' as const,
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  radioLabel: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '14px',
    color: '#333',
    cursor: 'pointer',
  },
  dayChips: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginTop: '4px',
  },
  dayChip: {
    padding: '8px 12px',
    fontSize: '12px',
    fontWeight: '600',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#d0d0d0',
    borderRadius: '20px',
    backgroundColor: '#fff',
    color: '#666',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  dayChipActive: {
    backgroundColor: '#3B82F6',
    color: '#fff',
    borderColor: '#3B82F6',
  },
  previewBadge: {
    padding: '12px 16px',
    backgroundColor: '#EFF6FF',
    borderRadius: '8px',
    border: '1px solid #BFDBFE',
    fontSize: '14px',
    fontWeight: '600',
    color: '#1E40AF',
    textAlign: 'center' as const,
  },
  schedulePreview: {
    margin: 0,
    fontSize: '13px',
    color: '#3B82F6',
    fontWeight: '500',
  },
  helperText: {
    margin: 0,
    fontSize: '13px',
    color: '#888',
    textAlign: 'center',
  },
  thumbnailPreview: {
    width: '120px',
    aspectRatio: '3/4',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: '#f0f0f0',
    marginTop: '8px',
  },
  thumbnailImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  error: {
    color: '#e53935',
    margin: 0,
    fontSize: '14px',
  },
  primaryButton: {
    padding: '14px 24px',
    backgroundColor: colors.accent.pink,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  scheduleButton: {
    backgroundColor: '#3B82F6',
  },
  activeShowBanner: {
    padding: '16px',
    backgroundColor: '#EFF6FF',
    borderRadius: '8px',
    border: '1px solid #BFDBFE',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  activeShowText: {
    margin: 0,
    fontSize: '14px',
    fontWeight: '500',
    color: '#1E40AF',
  },
  activeShowButton: {
    padding: '10px 16px',
    backgroundColor: '#3B82F6',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
};
